/**
 * AccountantDashboard — the canonical accountant "My Work" page.
 *
 * Task 4 (Phase 2) folded /queue/accountant into this page as three tabs:
 *   - Queue (available / unclaimed cases)
 *   - My Cases (claimed, not yet signed off)
 *   - Signed Off (already signed off)
 *
 * Layout:
 *   ┌─────────────────────────────────────────────────────┐
 *   │ Stats row (4 cards)                                 │
 *   ├────────────────────────────────┬────────────────────┤
 *   │ Tabs: [Queue] [Mine] [Signed]  │ Sidebar widgets    │
 *   │ <Active tab's case list>       │ scoped to          │
 *   │                                │ selectedCaseId     │
 *   └────────────────────────────────┴────────────────────┘
 *
 * Tab state lives in the URL as ?tab=queue|mine|signed. Sign-off modal + toast
 * live here (parent) so they survive tab switches.
 *
 * NOTE: This is the INTERNAL accountant view (main app auth). The EXTERNAL
 * accountant portal uses its own AccountantAuthContext at /accountant/*.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth, useUser } from '../../contexts/TestAuthContext';
import { api } from '../../utils/api';
import { FinancialValue } from '../../components/shared/FinancialValue';
import { formatCurrency } from '../../utils/financialFormat';
import RoleDashboardLayout from '../../components/RoleDashboardLayout';
import NowLineHeader, { NowLineStats } from '../../components/NowLineHeader';
import ForensicInsightsCard from '../../components/dashboard/ForensicInsightsCard';
import InsuranceQuestionsCard from '../../components/dashboard/InsuranceQuestionsCard';
import NextActionsChecklist from '../../components/dashboard/NextActionsChecklist';
import IndustryBenchmarkCard from '../../components/dashboard/IndustryBenchmarkCard';
import AIActivityCard from '../../components/dashboard/AIActivityCard';
import QueueTab from '../../components/accountant/QueueTab';
import MyCasesTab from '../../components/accountant/MyCasesTab';
import SignedOffTab from '../../components/accountant/SignedOffTab';
import SignOffToast from '../../components/accountant/SignOffToast';
import SignOffReadinessCard from '../../components/accountant/SignOffReadinessCard';
import DataQualityCard from '../../components/accountant/DataQualityCard';
import WarRoomSummaryCard from '../../components/accountant/WarRoomSummaryCard';
import {
  AccountantTabKey,
  CaseSummary,
} from '../../components/accountant/types';
import {
  FileText,
  DollarSign,
  CheckCircle,
  AlertTriangle,
  Unlock,
  User,
  X,
  LayoutGrid,
  ExternalLink,
  ClipboardCheck,
  Download,
} from 'lucide-react';

interface QueueStats {
  total_cases: number;
  needs_attention: number;
  no_action_needed: number;
  accountant: number;
  ready: number;
  total_value: number;
}

const TAB_KEYS: AccountantTabKey[] = ['queue', 'mine', 'signed'];
const DEFAULT_TAB: AccountantTabKey = 'queue';

// Aliases the URL param can use for the same canonical tab. The "signed" tab
// has been linked to as ?tab=signed-off and ?tab=signed_off in places (and
// "signed_off" matches the backend field name) — accept both rather than
// silently falling back to the default tab (audit 2026-04-25 F2.16).
const TAB_ALIASES: Record<string, AccountantTabKey> = {
  'signed-off': 'signed',
  'signed_off': 'signed',
  'signedoff': 'signed',
};

function parseTab(value: string | null): AccountantTabKey {
  if (!value) return DEFAULT_TAB;
  if (TAB_KEYS.includes(value as AccountantTabKey)) {
    return value as AccountantTabKey;
  }
  const alias = TAB_ALIASES[value.toLowerCase()];
  if (alias) return alias;
  return DEFAULT_TAB;
}

export default function AccountantDashboard() {
  const { userId } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab: AccountantTabKey = parseTab(searchParams.get('tab'));

  const currentAccountantName =
    (user as any)?.fullName ||
    (user as any)?.primaryEmailAddress?.emailAddress ||
    'Accountant';

  const setActiveTab = useCallback(
    (tab: AccountantTabKey) => {
      const next = new URLSearchParams(searchParams);
      next.set('tab', tab);
      setSearchParams(next, { replace: false });
    },
    [searchParams, setSearchParams],
  );

  const currentAccountantId = userId || user?.id || 'current-user';

  const [stats, setStats] = useState<QueueStats | null>(null);
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);

  // Task 5: batched sidebar payload.
  // Replaces five serial fetches (one per widget) with a single call to
  // /api/v1/dashboard/case-details/{id}. AbortController guarantees a
  // newer case-select cancels an in-flight older fetch instead of letting
  // stale data clobber the fresh selection on slow networks.
  const [caseDetails, setCaseDetails] = useState<any | null>(null);
  const [caseDetailsLoading, setCaseDetailsLoading] = useState(false);
  const [caseDetailsError, setCaseDetailsError] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedCaseId) {
      setCaseDetails(null);
      setCaseDetailsError(null);
      setCaseDetailsLoading(false);
      return;
    }
    const controller = new AbortController();
    setCaseDetailsLoading(true);
    setCaseDetailsError(null);
    api
      .get(`/api/v1/dashboard/case-details/${selectedCaseId}`, {
        signal: controller.signal,
        showErrorToast: false,
      })
      .then((data: any) => {
        if (controller.signal.aborted) return;
        setCaseDetails(data || null);
      })
      .catch((err: any) => {
        // Ignore aborts — they mean a newer selection superseded us.
        if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return;
        console.debug('case-details fetch failed:', err);
        setCaseDetailsError(err?.message || 'Failed to load case details');
        setCaseDetails(null);
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setCaseDetailsLoading(false);
      });
    return () => {
      controller.abort();
    };
  }, [selectedCaseId]);

  // ── Accountant-payload: sign-off readiness, data quality, war room summary ──
  // Second parallel sidebar fetch. Fires alongside /case-details but hits a
  // separate endpoint that computes heavier per-case diagnostics. AbortController
  // mirrors the caseDetails pattern so a newer selection cancels an in-flight one.
  const [accountantPayload, setAccountantPayload] = useState<any | null>(null);
  const [accountantPayloadLoading, setAccountantPayloadLoading] = useState(false);

  useEffect(() => {
    if (!selectedCaseId) {
      setAccountantPayload(null);
      setAccountantPayloadLoading(false);
      return;
    }
    const controller = new AbortController();
    setAccountantPayloadLoading(true);
    api
      .get(`/api/v1/dashboard/accountant-payload/${selectedCaseId}`, {
        signal: controller.signal,
        showErrorToast: false,
      })
      .then((data: any) => {
        if (controller.signal.aborted) return;
        setAccountantPayload(data || null);
      })
      .catch((err: any) => {
        if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return;
        console.debug('accountant-payload fetch failed:', err);
        setAccountantPayload(null);
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setAccountantPayloadLoading(false);
      });
    return () => {
      controller.abort();
    };
  }, [selectedCaseId]);

  // Now-Line urgency banner state. Mirrors the pre-Task-4 AccountantQueue page
  // so the header quantifies "X cases need attention now" above the stat row
  // and deep-links into the right tab.
  const [nowLineStats, setNowLineStats] = useState<NowLineStats>({
    needsAttention: { count: 0, breakdown: [] },
    noActionNeeded: { count: 0, breakdown: [] },
  });
  const [nowLineLoading, setNowLineLoading] = useState(true);

  // Sign-off state — lifted here so the toast survives tab switches.
  const [signingOffCaseId, setSigningOffCaseId] = useState<string | null>(null);
  const [showSignOffToast, setShowSignOffToast] = useState(false);
  const [signedOffBusiness, setSignedOffBusiness] = useState('');
  const [signOffError, setSignOffError] = useState<string | null>(null);

  // Track which case IDs each tab is currently rendering. When the active
  // tab's visible set no longer contains selectedCaseId, clear the selection.
  const [visibleIdsByTab, setVisibleIdsByTab] = useState<
    Record<AccountantTabKey, string[]>
  >({ queue: [], mine: [], signed: [] });

  const reportVisibleIds = useCallback(
    (tab: AccountantTabKey) => (ids: string[]) => {
      setVisibleIdsByTab((prev) => {
        const current = prev[tab];
        if (
          current.length === ids.length &&
          current.every((id, i) => id === ids[i])
        ) {
          return prev;
        }
        return { ...prev, [tab]: ids };
      });
    },
    [],
  );

  // Clear selectedCaseId when the active tab's visible list doesn't contain it.
  useEffect(() => {
    if (!selectedCaseId) return;
    const visible = visibleIdsByTab[activeTab];
    if (visible.length === 0) return; // still loading
    if (!visible.includes(selectedCaseId)) {
      setSelectedCaseId(null);
    }
  }, [activeTab, selectedCaseId, visibleIdsByTab]);

  const fetchStats = useCallback(async () => {
    try {
      const data = await api.get<QueueStats>(
        '/api/v1/queue/stats?user_role=accountant',
        { showErrorToast: false },
      );
      setStats(data);
    } catch (err) {
      console.warn('Failed to load accountant stats:', err);
    }
  }, []);

  const fetchNowLineStats = useCallback(async () => {
    setNowLineLoading(true);
    try {
      const data = await api.get<{
        needs_attention: { count: number; breakdown: { label: string; count: number }[] };
        no_action_needed: { count: number; breakdown: { label: string; count: number }[] };
      }>('/api/v1/queue/nowline-stats?role=accountant', {
        showErrorToast: false,
      });
      setNowLineStats({
        needsAttention: {
          count: data?.needs_attention?.count || 0,
          breakdown: data?.needs_attention?.breakdown || [],
        },
        noActionNeeded: {
          count: data?.no_action_needed?.count || 0,
          breakdown: data?.no_action_needed?.breakdown || [],
        },
      });
    } catch (err) {
      console.debug('Now-Line stats not available:', err);
    } finally {
      setNowLineLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchNowLineStats();
  }, [fetchStats, fetchNowLineStats]);

  const handleRequestSignOff = useCallback(
    async (caseItem: CaseSummary) => {
      setSigningOffCaseId(caseItem.id);
      setSignOffError(null);
      try {
        const response = await api.post<{
          success?: boolean;
          blocking_issues?: string[];
          message?: string;
        }>(`/api/v1/case-signoff/${caseItem.id}`, {
          notes: '',
          override_warnings: false,
        });
        if (response && response.success) {
          setSignedOffBusiness(caseItem.business_name || 'Case');
          setShowSignOffToast(true);
          // Refresh stats so the "Ready" count updates.
          await fetchStats();
        } else {
          const issues =
            (response?.blocking_issues && response.blocking_issues.join(', ')) ||
            response?.message ||
            'Unknown sign-off error';
          setSignOffError(`Cannot sign off: ${issues}`);
        }
      } catch (err: any) {
        setSignOffError(err?.message || 'Failed to sign off');
      } finally {
        setSigningOffCaseId(null);
      }
    },
    [fetchStats],
  );

  const tabButtonClass = (key: AccountantTabKey) =>
    `cs-label px-4 py-3 border-b-2 transition-colors min-h-[44px] ${
      activeTab === key ? '' : 'border-transparent'
    }`;

  const tabButtonStyle = (key: AccountantTabKey): React.CSSProperties =>
    activeTab === key
      ? { borderBottomColor: 'var(--color-brand)', color: 'var(--color-brand)' }
      : { color: 'var(--color-text-muted)' };

  const sharedTabProps = useMemo(
    () => ({
      selectedCaseId,
      onSelectCase: setSelectedCaseId,
      onRequestSignOff: handleRequestSignOff,
      signingOffCaseId,
      currentAccountantId,
    }),
    [selectedCaseId, handleRequestSignOff, signingOffCaseId, currentAccountantId],
  );

  return (
    <RoleDashboardLayout role="accountant">
      <div className="p-6 lg:p-8 max-w-[1600px] mx-auto">
        <SignOffToast
          show={showSignOffToast}
          businessName={signedOffBusiness}
          onClose={() => setShowSignOffToast(false)}
        />

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="cs-display">Accountant Dashboard</h1>
            <p className="cs-meta mt-1">
              Review calculations, verify data, sign off on cases
            </p>
          </div>
        </div>

        {/* Sign-off error banner */}
        {signOffError && (
          <div
            className="cs-body mb-4 p-3 rounded-lg flex items-center gap-2"
            style={{
              background: 'var(--color-needs-human-tint)',
              border: '1px solid var(--color-needs-human-tint-bd)',
              color: 'var(--color-needs-human-text)',
            }}
          >
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span className="cs-body flex-1">{signOffError}</span>
            <button
              onClick={() => setSignOffError(null)}
              className="flex-shrink-0"
              style={{ color: 'var(--color-needs-human-text)' }}
              aria-label="Dismiss sign-off error"
            >
              ✕
            </button>
          </div>
        )}

        {/* Now-Line urgency banner — re-added after Task 4's merge dropped it.
            Drives sign-off workflow by surfacing "X cases need attention now"
            and deep-linking into the right tab. */}
        <NowLineHeader
          stats={nowLineStats}
          userName={currentAccountantName}
          loading={nowLineLoading}
          onViewNeedsAttention={() => setActiveTab('queue')}
          onViewNoAction={() => setActiveTab('mine')}
        />

        {/* Stats Row — drillable buttons (BUG-3 cleanup, 2026-04-25).
            All four cards switch the active tab and apply a status filter
            via the URL (?tab=...&filter=...). QueueTab + MyCasesTab read the
            filter param so the back button works and the existing combobox
            stays in sync. */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <button
            type="button"
            onClick={() => {
              const params = new URLSearchParams(searchParams);
              params.set('tab', 'queue');
              params.delete('filter');
              setSearchParams(params, { replace: false });
            }}
            data-testid="kpi-available"
            aria-label={`View ${stats?.accountant || 0} available cases`}
            className="rounded-xl p-5
                       text-left w-full transition-all"
            style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
          >
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg" style={{ background: 'var(--color-needs-human-tint)' }}>
                <FileText className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
              </div>
              <div>
                <p className="cs-figure-lg">
                  {stats?.accountant || 0}
                </p>
                <p className="cs-meta">Available</p>
              </div>
            </div>
          </button>
          <button
            type="button"
            onClick={() => {
              const params = new URLSearchParams(searchParams);
              params.set('tab', 'queue');
              params.set('filter', 'needs-review');
              setSearchParams(params, { replace: false });
            }}
            data-testid="kpi-need-review"
            aria-label={`Filter to ${stats?.needs_attention || 0} cases needing review`}
            className="rounded-xl p-5
                       text-left w-full transition-all"
            style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
          >
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg" style={{ background: 'var(--color-needs-human-tint)' }}>
                <AlertTriangle className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
              </div>
              <div>
                <p className="cs-figure-lg">
                  {stats?.needs_attention || 0}
                </p>
                <p className="cs-meta">Need Review</p>
              </div>
            </div>
          </button>
          <button
            type="button"
            onClick={() => {
              const params = new URLSearchParams(searchParams);
              params.set('tab', 'queue');
              params.set('filter', 'ready');
              setSearchParams(params, { replace: false });
            }}
            data-testid="kpi-ready"
            aria-label={`Filter to ${stats?.ready || 0} cases ready for sign-off`}
            className="rounded-xl p-5
                       text-left w-full transition-all"
            style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
          >
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg" style={{ background: 'transparent' }}>
                <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
              </div>
              <div>
                <p className="cs-figure-lg">{stats?.ready || 0}</p>
                <p className="cs-meta">Ready</p>
              </div>
            </div>
          </button>
          <button
            type="button"
            onClick={() => {
              const params = new URLSearchParams(searchParams);
              params.set('tab', 'mine');
              params.delete('filter');
              setSearchParams(params, { replace: false });
            }}
            data-testid="kpi-portfolio"
            aria-label={`Switch to my cases (portfolio total ${formatCurrency(stats?.total_value)})`}
            className="rounded-xl p-5
                       text-left w-full transition-all"
            style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
          >
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg" style={{ background: 'var(--color-needs-human-tint)' }}>
                <DollarSign className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
              </div>
              <div>
                <p className="cs-figure-lg">
                  <FinancialValue value={stats?.total_value} />
                </p>
                <p className="cs-meta">Portfolio</p>
              </div>
            </div>
          </button>
        </div>

        {/* Main Content: Tabs + Sidebar */}
        <div className="flex gap-6">
          <div className="flex-1 min-w-0">
            {/* Tabs */}
            <div
              className="flex items-center gap-4 mb-6"
              style={{ borderBottom: '1px solid var(--color-border)' }}
              role="tablist"
              aria-label="Accountant case tabs"
            >
              <button
                type="button"
                onClick={() => setActiveTab('queue')}
                role="tab"
                id="tab-queue"
                aria-selected={activeTab === 'queue'}
                aria-controls="tabpanel-queue"
                tabIndex={activeTab === 'queue' ? 0 : -1}
                data-testid="tab-queue"
                className={tabButtonClass('queue')}
                style={tabButtonStyle('queue')}
              >
                <div className="flex items-center gap-2">
                  <Unlock className="w-4 h-4" aria-hidden="true" />
                  Queue
                </div>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('mine')}
                role="tab"
                id="tab-mine"
                aria-selected={activeTab === 'mine'}
                aria-controls="tabpanel-mine"
                tabIndex={activeTab === 'mine' ? 0 : -1}
                data-testid="tab-mine"
                className={tabButtonClass('mine')}
                style={tabButtonStyle('mine')}
              >
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" aria-hidden="true" />
                  My Cases
                </div>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('signed')}
                role="tab"
                id="tab-signed"
                aria-selected={activeTab === 'signed'}
                aria-controls="tabpanel-signed"
                tabIndex={activeTab === 'signed' ? 0 : -1}
                data-testid="tab-signed"
                className={tabButtonClass('signed')}
                style={tabButtonStyle('signed')}
              >
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" aria-hidden="true" />
                  Signed Off
                </div>
              </button>
            </div>

            {/* Active tab panel. Each tab manages its own fetch/state and
                reports its visible IDs back so we can clear selection. */}
            {activeTab === 'queue' && (
              <QueueTab
                {...sharedTabProps}
                onVisibleCaseIdsChange={reportVisibleIds('queue')}
              />
            )}
            {activeTab === 'mine' && (
              <MyCasesTab
                {...sharedTabProps}
                onVisibleCaseIdsChange={reportVisibleIds('mine')}
              />
            )}
            {activeTab === 'signed' && (
              <SignedOffTab
                {...sharedTabProps}
                onVisibleCaseIdsChange={reportVisibleIds('signed')}
              />
            )}
          </div>

          {/* Sidebar: Accountant Widgets (scoped to selectedCaseId) */}
          <div className="w-80 flex-shrink-0 space-y-4 hidden lg:block max-h-[calc(100vh-200px)] overflow-y-auto">
            {selectedCaseId ? (
              <>
                {/* Close header — explicit dismissal because row clicks are
                    now strict-select (no toggle). BUG-4 cleanup PR. */}
                <div className="flex items-center justify-between px-1">
                  <span className="cs-label">
                    Case Insights
                  </span>
                  <button
                    type="button"
                    onClick={() => setSelectedCaseId(null)}
                    aria-label="Close case insights panel"
                    data-testid="close-side-panel"
                    className="p-1 rounded"
                    style={{ color: 'var(--color-text-dim)' }}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* ── Primary tool CTAs ── */}
                {/* P&L Workbench — primary */}
                <button
                  type="button"
                  onClick={() => navigate(`/dashboard/accountant/workbench/${selectedCaseId}`)}
                  data-testid="open-workbench-btn"
                  className="cs-label w-full flex items-center justify-between px-4 py-3
                             rounded-xl transition-colors"
                  style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                >
                  <span className="flex items-center gap-2">
                    <LayoutGrid className="w-4 h-4" />
                    Open P&L Workbench
                  </span>
                  <ExternalLink className="w-4 h-4 opacity-70" />
                </button>

                {/* Engine Review + Export Center — secondary, 2-col */}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => navigate(`/dashboard/accountant/review/${selectedCaseId}`)}
                    data-testid="open-engine-review-btn"
                    className="cs-chip flex items-center justify-center gap-1.5 px-3 py-2.5
                               rounded-xl transition-colors"
                    style={{
                      background: 'var(--color-bg-surface)',
                      border: '1px solid var(--color-border)',
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    <ClipboardCheck className="w-3.5 h-3.5" style={{ color: 'var(--color-brand)' }} />
                    Engine Review
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(`/dashboard/accountant/export/${selectedCaseId}`)}
                    data-testid="open-export-center-btn"
                    className="cs-chip flex items-center justify-center gap-1.5 px-3 py-2.5
                               rounded-xl transition-colors"
                    style={{
                      background: 'var(--color-bg-surface)',
                      border: '1px solid var(--color-border)',
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    <Download className="w-3.5 h-3.5" style={{ color: 'var(--color-brand)' }} />
                    Export Center
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(`/dashboard/accountant/anomalies/${selectedCaseId}`)}
                    data-testid="open-anomaly-review-btn"
                    className="cs-chip flex items-center justify-center gap-1.5 px-3 py-2.5
                               rounded-xl transition-colors"
                    style={{
                      background: 'var(--color-bg-surface)',
                      border: '1px solid var(--color-border)',
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    <AlertTriangle className="w-3.5 h-3.5" style={{ color: 'var(--color-brand)' }} />
                    Anomaly Review
                  </button>
                </div>
                {/* ── Accountant-payload cards (sign-off, quality, war room) ── */}
                <SignOffReadinessCard
                  signOffReady={accountantPayload?.sign_off_ready ?? null}
                  signOffBlockers={accountantPayload?.sign_off_blockers ?? null}
                  isLoading={accountantPayloadLoading}
                />
                <DataQualityCard
                  data={accountantPayload?.data_quality ?? null}
                  isLoading={accountantPayloadLoading}
                />
                <WarRoomSummaryCard
                  data={accountantPayload?.war_room_summary ?? null}
                  isLoading={accountantPayloadLoading}
                />

                {/* ── Case-details cards (forensic, insurance, actions, benchmark, AI) ── */}
                <ForensicInsightsCard
                  reportId={selectedCaseId}
                  data={caseDetails?.forensic_insights ?? (caseDetailsError ? { error: caseDetailsError } : null)}
                  isLoading={caseDetailsLoading}
                />
                <InsuranceQuestionsCard
                  reportId={selectedCaseId}
                  data={caseDetails?.insurance_questions ?? (caseDetailsError ? { error: caseDetailsError } : null)}
                  isLoading={caseDetailsLoading}
                />
                <NextActionsChecklist
                  reportId={selectedCaseId}
                  data={caseDetails?.next_actions ?? (caseDetailsError ? { error: caseDetailsError } : null)}
                  isLoading={caseDetailsLoading}
                />
                <IndustryBenchmarkCard
                  reportId={selectedCaseId}
                  data={caseDetails?.industry_benchmark ?? (caseDetailsError ? { error: caseDetailsError } : null)}
                  isLoading={caseDetailsLoading}
                />
                <AIActivityCard
                  reportId={selectedCaseId}
                  data={caseDetails?.ai_activity ?? (caseDetailsError ? { error: caseDetailsError } : null)}
                  isLoading={caseDetailsLoading}
                />
              </>
            ) : (
              <div
                className="rounded-xl p-6 text-center"
                style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
              >
                <div className="mb-2" style={{ color: 'var(--color-text-dim)' }}>
                  <CheckCircle className="w-10 h-10 mx-auto" />
                </div>
                <h3 className="cs-h3 mb-1">Select a Case</h3>
                <p className="cs-meta">
                  Click a case to view forensic insights, adjuster questions, and
                  industry benchmarks.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </RoleDashboardLayout>
  );
}
